import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';

const CalculatorMatematic = () => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [history, setHistory] = useState([]);

  // Încarcă istoricul din localStorage la inițializare
  useEffect(() => {
    const savedHistory = localStorage.getItem('calculatorHistory');
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Salvează istoricul în localStorage când se schimbă
  useEffect(() => {
    localStorage.setItem('calculatorHistory', JSON.stringify(history));
  }, [history]);

  // Suport pentru tastatura
  useEffect(() => {
    const handleKeyPress = (event) => {
      const { key } = event;
      
      // Previne comportamentul default pentru anumite taste
      if (['Enter', '=', 'Escape', 'Delete', 'Backspace'].includes(key)) {
        event.preventDefault();
      }

      // Numere
      if (/[0-9]/.test(key)) {
        inputNumber(parseInt(key));
      }
      // Operații
      else if (key === '+') {
        performOperation('+');
      }
      else if (key === '-') {
        performOperation('-');
      }
      else if (key === '*' || key === 'x' || key === 'X') {
        performOperation('×');
      }
      else if (key === '/' || key === '÷') {
        performOperation('÷');
      }
      // Egal
      else if (key === 'Enter' || key === '=') {
        handleEquals();
      }
      // Virgulă/punct
      else if (key === '.' || key === ',') {
        inputDecimal();
      }
      // Clear
      else if (key === 'Escape' || key === 'Delete') {
        clear();
      }
      // Backspace (șterge ultima cifră)
      else if (key === 'Backspace') {
        handleBackspace();
      }
      // Procent
      else if (key === '%') {
        handlePercentage();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [display, previousValue, operation, waitingForOperand]);

  const handleBackspace = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const addToHistory = (calculation, result) => {
    const newEntry = {
      id: Date.now(),
      calculation,
      result,
      timestamp: new Date().toLocaleTimeString('ro-RO')
    };
    setHistory(prev => [newEntry, ...prev.slice(0, 19)]); // Păstrează ultimele 20 de calcule
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('calculatorHistory');
  };

  const inputNumber = (num) => {
    if (waitingForOperand) {
      setDisplay(String(num));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(num) : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const performOperation = (nextOperation) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue, secondValue, operation) => {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return secondValue !== 0 ? firstValue / secondValue : 0;
      case '=':
        return secondValue;
      default:
        return secondValue;
    }
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      const calculationString = `${previousValue} ${operation} ${inputValue} = ${newValue}`;
      
      // Adaugă în istoric
      addToHistory(calculationString, newValue);
      
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const handlePercentage = () => {
    const value = parseFloat(display);
    const result = value / 100;
    addToHistory(`${value}% = ${result}`, result);
    setDisplay(String(result));
  };

  const handleSquareRoot = () => {
    const value = parseFloat(display);
    if (value >= 0) {
      const result = Math.sqrt(value);
      addToHistory(`√${value} = ${result}`, result);
      setDisplay(String(result));
    }
  };

  const handlePower = () => {
    const value = parseFloat(display);
    const result = value * value;
    addToHistory(`${value}² = ${result}`, result);
    setDisplay(String(result));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <CalculateIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Calculator Matematic
      </Typography>
      
      <Grid container spacing={3}>
        {/* Calculator principal */}
        <Grid item xs={12} md={8}>
          <Card sx={{ marginTop: 2 }}>
            <CardContent>
              <Paper 
                elevation={1}
                sx={{ 
                  padding: 2, 
                  marginBottom: 2, 
                  backgroundColor: '#000',
                  color: '#00ff00',
                  fontFamily: 'monospace',
                  textAlign: 'right',
                  minHeight: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    fontSize: display.length > 10 ? '1.5rem' : '2rem'
                  }}
                >
                  {display}
                </Typography>
              </Paper>

              <Grid container spacing={1}>
                {/* Rândul 1 */}
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={clear}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    C
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="info"
                    onClick={handlePercentage}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    %
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="info"
                    onClick={handleSquareRoot}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    √
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    onClick={() => performOperation('÷')}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    ÷
                  </Button>
                </Grid>

                {/* Rândul 2 */}
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(7)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    7
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(8)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    8
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(9)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    9
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    onClick={() => performOperation('×')}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    ×
                  </Button>
                </Grid>

                {/* Rândul 3 */}
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(4)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    4
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(5)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    5
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(6)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    6
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    onClick={() => performOperation('-')}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    -
                  </Button>
                </Grid>

                {/* Rândul 4 */}
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(1)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    1
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(2)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    2
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(3)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    3
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    onClick={() => performOperation('+')}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    +
                  </Button>
                </Grid>

                {/* Rândul 5 */}
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => inputNumber(0)}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    0
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={inputDecimal}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    .
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="info"
                    onClick={handlePower}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    x²
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    onClick={handleEquals}
                    sx={{ height: '50px', fontSize: '1rem' }}
                  >
                    =
                  </Button>
                </Grid>
              </Grid>

              <Box sx={{ marginTop: 2, padding: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary" align="center">
                  Calculator matematic cu suport pentru tastatura. Folosește tastele numerice, +, -, *, /, Enter (=), Escape (Clear), Backspace
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Istoric calcule */}
        <Grid item xs={12} md={4}>
          <Card sx={{ marginTop: 2, height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <HistoryIcon sx={{ marginRight: 1 }} />
                  Istoric Calcule
                </Typography>
                <Tooltip title="Golește istoricul">
                  <IconButton 
                    onClick={clearHistory} 
                    color="error" 
                    size="small"
                    disabled={history.length === 0}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {history.length === 0 ? (
                <Box sx={{ textAlign: 'center', padding: 3, color: 'text.secondary' }}>
                  <HistoryIcon sx={{ fontSize: 48, opacity: 0.3, marginBottom: 1 }} />
                  <Typography variant="body2">
                    Niciun calcul efectuat încă
                  </Typography>
                </Box>
              ) : (
                <Paper 
                  elevation={1} 
                  sx={{ 
                    maxHeight: 400, 
                    overflow: 'auto',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <List dense>
                    {history.map((entry, index) => (
                      <Box key={entry.id}>
                        <ListItem 
                          sx={{ 
                            padding: '8px 12px',
                            '&:hover': {
                              backgroundColor: '#e3f2fd',
                              cursor: 'pointer'
                            }
                          }}
                          onClick={() => setDisplay(String(entry.result))}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                {entry.calculation}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="textSecondary">
                                {entry.timestamp}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < history.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </List>
                </Paper>
              )}

              {history.length > 0 && (
                <Box sx={{ marginTop: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    Click pe un calcul pentru a folosi rezultatul
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CalculatorMatematic;